import {
  Card,
  Checkbox,
  Col,
  Empty,
  Form,
  FormInstance,
  Row,
  Select,
} from 'antd';
import { useDefaultEmptyFieldError } from '@/hooks';
import { FloatingFormItem, InputDecimal } from '@/components/_common';
import {
  InvoiceIcmsCstCsosn,
  TaxRegimeEnum,
} from '@/core/domain/invoice/invoiceNoteItems/invoiceNoteItemsParts/InvoiceNoteItems.types';
import TaxRegimeSelect from '@/components/_common/TaxRegimeSelect';
import {
  TaxeIcmsCsosnSelect,
  TaxeIcmsCstSelect,
  TaxeIcmsOriginSelect,
} from '@/components/_common/TaxesSelect';
import { useCallback } from 'react';
import { InvoiceCstCsosnCodes } from '@/core/domain/invoice/invoiceNoteItems/invoiceNoteItemsParts/InvoiceNoteItems.const';
import TaxIcmsBcModalitySelect from '@/components/_common/TaxIcmsBcModalitySelect';
import TaxIcmsBcStModalitySelect from '@/components/_common/TaxIcmsBcStModalitySelect';
import { InvoicePartFormValues } from '../InvoicePartForm.types';
import { icmsExemptionReasonOptions } from './options';
import { useInvoicePartCstCsosnChangeFillFieds } from '../InvoicePart.hooks';
import { useInvoiceFormContext } from '../../../InvoiceFormProvider';
import ModalLoadingOverlay from '@/components/_common/ModalLoadingOverlay';

interface Props {
  form: FormInstance<InvoicePartFormValues>;
  isUpdateTaxes?: boolean;
  invoiceItemId?: number;
}

const InvoicePartIcmsForm: React.FC<Props> = ({
  form,
  isUpdateTaxes = false,
  invoiceItemId,
}) => {
  const formValues = Form.useWatch([], form);

  const {
    invoice,
    disabledFields,
    isNfc,
    isNfe,
    invoiceSettingNfc,
    invoiceSettingNfe,
  } = useInvoiceFormContext();

  const icsmTaxRegime = formValues?.itemTaxes?.icms.taxRegime;
  const icmsSimpleNationalCreditPercentage =
    formValues?.itemTaxes?.icms.icmsSimpleNationalCreditPercentage;

  const required = useDefaultEmptyFieldError();

  const visibleField = useCallback(
    (rules: string[]) => {
      return (
        rules &&
        !!formValues?.itemTaxes?.icms.icmsCst &&
        rules.includes(
          InvoiceCstCsosnCodes[formValues?.itemTaxes?.icms.icmsCst],
        )
      );
    },
    [formValues?.itemTaxes?.icms.icmsCst],
  );

  const [cstCsosnHandle, isLoading] = useInvoicePartCstCsosnChangeFillFieds({
    companyId: invoice.data?.companyId,
  });

  const icmsCode = formValues?.itemTaxes?.icms?.icmsCst
    ? InvoiceCstCsosnCodes[formValues.itemTaxes.icms.icmsCst]
    : '';

  const isCsosn =
    icsmTaxRegime === TaxRegimeEnum.simplesNationalMei ||
    icsmTaxRegime === TaxRegimeEnum.simple;

  const handleSimpleNationalCredit = useCallback(
    (params: { taxRegime?: TaxRegimeEnum; icmsCst?: InvoiceIcmsCstCsosn }) => {
      const taxRegime =
        params.taxRegime ||
        (form.getFieldValue([
          'itemTaxes',
          'icms',
          'taxRegime',
        ]) as TaxRegimeEnum);
      const icmsCst =
        params.icmsCst ||
        (form.getFieldValue([
          'itemTaxes',
          'icms',
          'icmsCst',
        ]) as InvoiceIcmsCstCsosn);

      if (
        taxRegime === TaxRegimeEnum.simple &&
        [
          InvoiceIcmsCstCsosn.TAXATION_UNDER_SIMPLES_WITH_CREDIT_PERMISSION,
          InvoiceIcmsCstCsosn.SIMPLES_NACIONAL_WITH_CREDIT_PERMISSION_AND_ICMS_BY_TAX_SUBSTITUTION,
        ].includes(icmsCst)
      ) {
        if (isNfc) {
          form.setFieldValue(
            ['itemTaxes', 'icms', 'icmsSimpleNationalCreditPercentage'],
            invoiceSettingNfc?.simpleNationalCreditRateDefault,
          );
        }
        if (isNfe) {
          form.setFieldValue(
            ['itemTaxes', 'icms', 'icmsSimpleNationalCreditPercentage'],
            invoiceSettingNfe?.simpleNationalCreditRateDefault,
          );
        }
      }
    },
    [
      isNfc,
      isNfe,
      invoiceSettingNfc,
      invoiceSettingNfe,
      icmsSimpleNationalCreditPercentage,
    ],
  );

  return (
    <Form form={form} preserve={false} disabled={disabledFields}>
      {isLoading && (
        <ModalLoadingOverlay
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            backdropFilter: 'blur(1px)',
          }}
          messages={[
            `Configurando campos do ${isCsosn ? 'csosn' : 'cst'} "${icmsCode}"`,
          ]}
        />
      )}

      <Card title="ICMS" type="inner" style={{ marginBottom: 24 }}>
        <Row gutter={[24, 16]}>
          <Col xs={24} sm={24} md={12} lg={8}>
            <FloatingFormItem
              name={['itemTaxes', 'icms', 'taxRegime']}
              label="Regime tributário"
              rules={isUpdateTaxes ? undefined : [required]}
              getValueFromEvent={(value) => {
                const prevTaxRegime = icsmTaxRegime;

                if (
                  (prevTaxRegime !== value &&
                    prevTaxRegime === TaxRegimeEnum.simple) ||
                  ((prevTaxRegime === TaxRegimeEnum.normal ||
                    prevTaxRegime ===
                      TaxRegimeEnum.simpleNationalExcessSublimitGrossRevenue) &&
                    value === TaxRegimeEnum.simple)
                ) {
                  form.setFieldValue(
                    ['itemTaxes', 'icms', 'icmsCst'],
                    undefined,
                  );
                }

                if (value === TaxRegimeEnum.simple) {
                  handleSimpleNationalCredit({ taxRegime: value });
                }

                return value;
              }}
            >
              <TaxRegimeSelect />
            </FloatingFormItem>
          </Col>

          <Col xs={24} sm={24} md={12} lg={8}>
            <FloatingFormItem
              name={['itemTaxes', 'icms', 'icmsCst']}
              label={isCsosn ? 'CSOSN' : 'CST'}
              rules={isUpdateTaxes ? undefined : [required]}
              getValueFromEvent={(value, option) => {
                if (
                  ['10', '00', '20', '51', '70', '90', '900'].includes(
                    option.label,
                  )
                ) {
                  (async () => {
                    const result = await cstCsosnHandle({
                      ncmId: formValues.general?.ncmId,
                      invoiceType: invoice.data?.invoiceType,
                      invoiceItemId,
                    });
                    form.setFieldsValue(result);
                    await form.validateFields();
                  })();
                }

                if (
                  [
                    InvoiceIcmsCstCsosn.TAXATION_UNDER_SIMPLES_WITH_CREDIT_PERMISSION,
                    InvoiceIcmsCstCsosn.SIMPLES_NACIONAL_WITH_CREDIT_PERMISSION_AND_ICMS_BY_TAX_SUBSTITUTION,
                  ].includes(value)
                ) {
                  handleSimpleNationalCredit({
                    taxRegime: icsmTaxRegime,
                    icmsCst: value,
                  });
                }

                return value;
              }}
            >
              {icsmTaxRegime === TaxRegimeEnum.simple ||
              icsmTaxRegime === TaxRegimeEnum.simplesNationalMei ? (
                <TaxeIcmsCsosnSelect
                  isMei={icsmTaxRegime === TaxRegimeEnum.simplesNationalMei}
                />
              ) : (
                <TaxeIcmsCstSelect />
              )}
            </FloatingFormItem>
          </Col>

          <Col xs={24} sm={24} md={12} lg={8}>
            <FloatingFormItem
              name={['itemTaxes', 'icms', 'icmsOrigin']}
              label="Origem"
              rules={isUpdateTaxes ? undefined : [required]}
            >
              <TaxeIcmsOriginSelect />
            </FloatingFormItem>
          </Col>

          {visibleField(['101', '201']) && (
            <Col xs={24} sm={24} md={12} lg={8}>
              <FloatingFormItem
                name={[
                  'itemTaxes',
                  'icms',
                  'icmsSimpleNationalCreditPercentage',
                ]}
                label="Alíquota aplicável de cálculo do crédito"
              >
                <InputDecimal fractions={4} max={100} />
              </FloatingFormItem>
            </Col>
          )}

          {visibleField(['101', '201']) && (
            <Col xs={24} sm={24} md={12} lg={8}>
              <FloatingFormItem
                name={['itemTaxes', 'icms', 'icmsSimpleNationalCreditValue']}
                label="Valor crédito"
              >
                <InputDecimal />
              </FloatingFormItem>
            </Col>
          )}

          {visibleField(['10', '00', '20', '51', '70', '90', '900']) && (
            <Col xs={24} sm={24} md={12} lg={8}>
              <FloatingFormItem
                name={['itemTaxes', 'icms', 'icmsAliquot']}
                label="Alíquota do imposto"
              >
                <InputDecimal fractions={4} max={100} />
              </FloatingFormItem>
            </Col>
          )}

          {visibleField(['10', '00', '20', '51', '70', '90', '900']) && (
            <Col xs={24} sm={24} md={12} lg={8}>
              <FloatingFormItem
                name={[
                  'itemTaxes',
                  'icms',
                  'icmsBaseCalculationDeterminationMode',
                ]}
                label="Modalidade de determinação da BC"
              >
                <TaxIcmsBcModalitySelect />
              </FloatingFormItem>
            </Col>
          )}

          {visibleField(['10', '00', '20', '51', '70', '90', '900']) && (
            <Col xs={24} sm={24} md={12} lg={8}>
              <FloatingFormItem
                name={['itemTaxes', 'icms', 'icmsBaseCalculationValue']}
                label="Valor da base de cálculo"
              >
                <InputDecimal />
              </FloatingFormItem>
            </Col>
          )}

          {visibleField(['20', '51', '70', '90', '900']) && (
            <Col xs={24} sm={24} md={12} lg={8}>
              <FloatingFormItem
                name={[
                  'itemTaxes',
                  'icms',
                  'icmsBaseCalculationReductionPercentage',
                ]}
                label="Percentual da redução da BC"
              >
                <InputDecimal max={100} />
              </FloatingFormItem>
            </Col>
          )}

          {visibleField(['10', '20', '00', '51', '70', '90', '500', '900']) && (
            <Col xs={24} sm={24} md={12} lg={8}>
              <FloatingFormItem
                name={['itemTaxes', 'icms', 'icmsValue']}
                label="Valor do ICMS"
              >
                <InputDecimal fractions={4} />
              </FloatingFormItem>
            </Col>
          )}

          <Col xs={24} sm={24} md={12} lg={8}>
            <Form.Item
              name={['itemTaxes', 'icms', 'shouldNotDeductExemptIcms']}
              valuePropName="checked"
            >
              <Checkbox>ICMS desonerado não diminui no total da nota</Checkbox>
            </Form.Item>
          </Col>
        </Row>
      </Card>

      {!formValues?.itemTaxes?.icms.icmsCst && (
        <Empty
          description="Selecione o CST ou CSOSN"
          style={{ marginTop: 24, marginBottom: 24 }}
        />
      )}

      {visibleField([
        '00',
        '10',
        '30',
        '41',
        '60',
        '70',
        '90',
        '201',
        '202',
        '203',
        '500',
        '900',
      ]) && (
        <Card title="ICMS ST" type="inner" style={{ marginBottom: 24 }}>
          <Row gutter={[24, 16]}>
            {visibleField([
              '00',
              '10',
              '30',
              '41',
              '60',
              '70',
              '90',
              '201',
              '202',
              '203',
              '500',
              '900',
            ]) && (
              <Col xs={24} sm={24} md={12} lg={8}>
                <FloatingFormItem
                  name={['itemTaxes', 'icms', 'icmsStAliquot']}
                  label="Alíquota"
                >
                  <InputDecimal />
                </FloatingFormItem>
              </Col>
            )}

            {visibleField([
              '00',
              '10',
              '30',
              '70',
              '90',
              '201',
              '202',
              '203',
              '900',
            ]) && (
              <Col xs={24} sm={24} md={12} lg={8}>
                <FloatingFormItem
                  name={['itemTaxes', 'icms', 'icmsStDeterminationMode']}
                  label="Modalidade de determinação da BC do ICMS"
                >
                  <TaxIcmsBcStModalitySelect />
                </FloatingFormItem>
              </Col>
            )}

            {visibleField([
              '00',
              '10',
              '30',
              '41',
              '60',
              '70',
              '90',
              '201',
              '202',
              '203',
              '500',
              '900',
            ]) && (
              <Col xs={24} sm={24} md={12} lg={8}>
                <FloatingFormItem
                  name={['itemTaxes', 'icms', 'icmsStBaseCalculationValue']}
                  label="Valor da BC do ICMS ST"
                >
                  <InputDecimal />
                </FloatingFormItem>
              </Col>
            )}

            {visibleField(['10', '30', '90', '201', '202', '203', '900']) && (
              <Col xs={24} sm={24} md={12} lg={8}>
                <FloatingFormItem
                  name={[
                    'itemTaxes',
                    'icms',
                    'icmsStBaseCalculationReductionPercentage',
                  ]}
                  label="Percentual da Redução de BC"
                >
                  <InputDecimal />
                </FloatingFormItem>
              </Col>
            )}

            {visibleField([
              '10',
              '30',
              '70',
              '90',
              '201',
              '202',
              '203',
              '900',
            ]) && (
              <Col xs={24} sm={24} md={12} lg={8}>
                <FloatingFormItem
                  name={[
                    'itemTaxes',
                    'icms',
                    'icmsStValueAddedMarginPercentage',
                  ]}
                  label="(%)MVA"
                >
                  <InputDecimal fractions={4} />
                </FloatingFormItem>
              </Col>
            )}

            {visibleField(['60']) && (
              <Col xs={24} sm={24} md={12} lg={8}>
                <FloatingFormItem
                  name={[
                    'itemTaxes',
                    'icms',
                    'icmsDestinyUfBaseCalculationValue',
                  ]}
                  label="Valor da BC do ICMS ST da UF destino"
                >
                  <InputDecimal max={100} />
                </FloatingFormItem>
              </Col>
            )}

            {visibleField(['60']) && (
              <Col xs={24} sm={24} md={12} lg={8}>
                <FloatingFormItem
                  name={['itemTaxes', 'icms', 'icmsDestinyUfValue']}
                  label="Valor do ICMS ST da UF destino"
                >
                  <InputDecimal />
                </FloatingFormItem>
              </Col>
            )}

            {visibleField([
              '10',
              '30',
              '60',
              '70',
              '90',
              '201',
              '202',
              '203',
              '500',
              '900',
            ]) && (
              <Col xs={24} sm={24} md={12} lg={8}>
                <FloatingFormItem
                  name={['itemTaxes', 'icms', 'icmsStValue']}
                  label="Valor"
                >
                  <InputDecimal />
                </FloatingFormItem>
              </Col>
            )}
          </Row>
        </Card>
      )}

      {visibleField([
        '00',
        '10',
        '20',
        '30',
        '41',
        '51',
        '60',
        '70',
        '90',
        '201',
        '202',
        '203',
        '500',
        '900',
      ]) && (
        <Card title="ICMS ST FCP" type="inner" style={{ marginBottom: 24 }}>
          <Row gutter={[24, 16]}>
            {visibleField([
              '00',
              '10',
              '30',
              '41',
              '60',
              '70',
              '90',
              '201',
              '202',
              '203',
              '500',
              '900',
            ]) && (
              <Col xs={24} sm={24} md={12} lg={8}>
                <FloatingFormItem
                  name={['itemTaxes', 'icms', 'icmsStFcpAliquot']}
                  label="Valor Alíquota FCP ST (%)"
                >
                  <InputDecimal fractions={4} max={100} />
                </FloatingFormItem>
              </Col>
            )}

            {visibleField([
              '00',
              '10',
              '20',
              '30',
              '41',
              '51',
              '60',
              '70',
              '90',
              '201',
              '202',
              '203',
              '500',
              '900',
            ]) && (
              <Col xs={24} sm={24} md={12} lg={8}>
                <FloatingFormItem
                  name={['itemTaxes', 'icms', 'icmsStFcpBaseCalculationValue']}
                  label="Valor da Base de Cálculo do FCP retido por Substituição Tributária"
                >
                  <InputDecimal />
                </FloatingFormItem>
              </Col>
            )}

            {visibleField([
              '10',
              '60',
              '70',
              '90',
              '201',
              '202',
              '203',
              '500',
              '900',
            ]) && (
              <Col xs={24} sm={24} md={12} lg={8}>
                <FloatingFormItem
                  name={['itemTaxes', 'icms', 'icmsStFcpValue']}
                  label="Valor do FCP retido por Substituição Tributária"
                >
                  <InputDecimal />
                </FloatingFormItem>
              </Col>
            )}
          </Row>
        </Card>
      )}

      {visibleField(['00', '10', '20', '51', '70', '90']) && (
        <Card title="ICMS FCP" type="inner" style={{ marginBottom: 24 }}>
          <Row gutter={[24, 16]}>
            {visibleField(['00', '10', '20', '51', '70', '90']) && (
              <Col xs={24} sm={24} md={12} lg={8}>
                <FloatingFormItem
                  name={['itemTaxes', 'icms', 'icmsFcpAliquot']}
                  label="Percentual relativo ao Fundo de Combate à Pobreza (Alíquota)"
                >
                  <InputDecimal fractions={4} max={100} />
                </FloatingFormItem>
              </Col>
            )}

            {visibleField(['10', '20', '51', '70', '90']) && (
              <Col xs={24} sm={24} md={12} lg={8}>
                <FloatingFormItem
                  name={['itemTaxes', 'icms', 'icmsFcpBaseCalculationValue']}
                  label="Valor da Base de Cálculo do FCP"
                >
                  <InputDecimal />
                </FloatingFormItem>
              </Col>
            )}
          </Row>
        </Card>
      )}

      {visibleField(['20', '30', '40', '41', '50', '70']) && (
        <Card
          title="ICMS Desoneração"
          type="inner"
          style={{ marginBottom: 24 }}
        >
          <Row gutter={[24, 16]}>
            <Col xs={24} sm={24} md={12} lg={8}>
              <FloatingFormItem
                name={['itemTaxes', 'icms', 'icmsExemptionValue']}
                label="Valor da desoneração"
              >
                <InputDecimal />
              </FloatingFormItem>
            </Col>

            <Col xs={24} sm={24} md={12} lg={8}>
              <FloatingFormItem
                name={['itemTaxes', 'icms', 'icmsExemptionReason']}
                label="Motivo da desoneração"
              >
                <Select options={icmsExemptionReasonOptions} />
              </FloatingFormItem>
            </Col>
          </Row>
        </Card>
      )}

      {visibleField(['51']) && (
        <Card
          title="ICMS Diferimento"
          type="inner"
          style={{ marginBottom: 24 }}
        >
          <Row gutter={[24, 16]}>
            <Col xs={24} sm={24} md={12} lg={8}>
              <FloatingFormItem
                name={['itemTaxes', 'icms', 'icmsDefermentPercentage']}
                label="Percentual do diferimento"
              >
                <InputDecimal max={100} fractions={4} />
              </FloatingFormItem>
            </Col>

            <Col xs={24} sm={24} md={12} lg={8}>
              <FloatingFormItem
                name={['itemTaxes', 'icms', 'icmsDefermentValue']}
                label="Valor do ICMS diferido"
              >
                <InputDecimal />
              </FloatingFormItem>
            </Col>

            <Col xs={24} sm={24} md={12} lg={8}>
              <FloatingFormItem
                name={['itemTaxes', 'icms', 'icmsDefermentDueValue']}
                label="Valor do ICMS realmente devido"
              >
                <InputDecimal />
              </FloatingFormItem>
            </Col>
          </Row>
        </Card>
      )}

      {visibleField(['500']) && (
        <Card title="ICMS Efetivo" type="inner" style={{ marginBottom: 24 }}>
          <Row gutter={[24, 16]}>
            <Col xs={24} sm={24} md={12} lg={8}>
              <FloatingFormItem
                name={[
                  'itemTaxes',
                  'icms',
                  'icmsEffectiveBaseCalculationValue',
                ]}
                label="Valor do cálculo base efetivo"
              >
                <InputDecimal />
              </FloatingFormItem>
            </Col>

            <Col xs={24} sm={24} md={12} lg={8}>
              <FloatingFormItem
                name={[
                  'itemTaxes',
                  'icms',
                  'icmsEffectiveBaseCalculationReductionPercentage',
                ]}
                label="Percentual de redução"
              >
                <InputDecimal max={100} />
              </FloatingFormItem>
            </Col>

            <Col xs={24} sm={24} md={12} lg={8}>
              <FloatingFormItem
                name={['itemTaxes', 'icms', 'icmsEffectiveAliquot']}
                label="Áliquota"
              >
                <InputDecimal />
              </FloatingFormItem>
            </Col>

            <Col xs={24} sm={24} md={12} lg={8}>
              <FloatingFormItem
                name={['itemTaxes', 'icms', 'icmsEffectiveValue']}
                label="Valor"
              >
                <InputDecimal />
              </FloatingFormItem>
            </Col>
          </Row>
        </Card>
      )}
    </Form>
  );
};

export default InvoicePartIcmsForm;
